import { useState, type ComponentProps } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BarcodeScannerDialog } from './BarcodeScannerDialog';
import { normalizeBarcodeValue } from '@/lib/barcodeScan';
import { cn } from '@/lib/utils';
import { ScanLine } from 'lucide-react';

export interface BarcodeScanInputProps
  extends Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  scanTitle?: string;
  scanDescription?: string;
}

export function BarcodeScanInput({
  value,
  onChange,
  className,
  scanTitle,
  scanDescription,
  disabled,
  ...inputProps
}: BarcodeScanInputProps) {
  const [scannerOpen, setScannerOpen] = useState(false);

  return (
    <>
      <div className="relative">
        <Input
          {...inputProps}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={cn('pr-11 font-mono text-sm', className)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          className="absolute right-0 top-0 h-full rounded-l-none px-3 text-muted-foreground hover:text-foreground"
          onClick={() => setScannerOpen(true)}
          aria-label="Scan barcode with camera"
        >
          <ScanLine className="h-4 w-4" />
        </Button>
      </div>

      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        title={scanTitle}
        description={scanDescription}
        onScan={(scanned) => onChange(normalizeBarcodeValue(scanned))}
      />
    </>
  );
}
